import { useState } from 'react';
import type { CampaignRaceDto, PartyPrimary, RaceHitDto, RaceStageKind, WriteInRequest } from '@signalvote/shared';
import { placesOf, RacePrimaryStatus, RaceStanding } from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import { useMayManageCampaign } from '@/hooks';
import { useCampaignRace, useLinkRace, useSaveVoteGoal } from '@/hooks/queries';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PendingButton } from '@/components/ui/pending-button';
import { fmt, stageTitle } from './goalWords';
import { openingComplete, type OpeningEdit, type RaceOpening } from './RaceOpening';
import { stateOf, writeInOf } from './raceStages';
import { draftFromSaved, emptyDraft, reseatAll, reseated, sameAsSaved, saveRequestOf, workOut, type StageDraft } from './stageGoal';
import { VoteGoalCalculator } from './VoteGoalCalculator';

/** A catalog Race or a written-in one: exactly one is set, as the link request takes them. */
type RaceChoice = { raceId: string; writeIn: null } | { raceId: null; writeIn: WriteInRequest };

/** The campaign's Vote Goal: one sentence to pick the Race in, answer the primary, work the goal out and save it. */
export function VoteGoalPage() {
  const link = useCampaignRace();
  const { mutateAsync: linkRace, isPending: linking, error: linkError } = useLinkRace();
  const mayEdit = useMayManageCampaign();
  // A state picked with no race in it picked yet. Until one is, the link stays as it is.
  const [otherState, setOtherState] = useState<string | null>(null);
  const [switchTo, setSwitchTo] = useState<{ name: string; choice: RaceChoice } | null>(null);

  const linkTo = async (choice: RaceChoice, primary: PartyPrimary | null) => {
    await linkRace({ ...choice, primary, concurrencyVersion: link.data?.concurrencyVersion ?? null });
    setOtherState(null);
  };
  const pick = (race: RaceHitDto) => {
    if (link.data?.raceId === race.id) setOtherState(null);
    else if (link.data) setSwitchTo({ name: race.name, choice: { raceId: race.id, writeIn: null } });
    else void linkTo({ raceId: race.id, writeIn: null }, null).catch(() => undefined);
  };
  // Editing the race the candidate wrote in changes no goals' race, so only a switch from a catalog race asks first.
  const writeIn = (writeIn: WriteInRequest) => {
    const choice: RaceChoice = { raceId: null, writeIn };
    if (link.data && !link.data.writtenIn) setSwitchTo({ name: writeIn.name, choice });
    else void linkTo(choice, null).catch(() => undefined);
  };
  const keepRace = () => {
    setOtherState(null);
    setSwitchTo(null);
  };

  const linkedState = link.data ? stateOf(link.data.raceId) : null;
  const edit = (current: CampaignRaceDto | null): OpeningEdit | null =>
    mayEdit
      ? {
          onState: (state) => setOtherState(state === linkedState ? null : state),
          race: { currentRaceId: current && !current.writtenIn ? current.raceId : null, writeIn: current && writeInOf(current), onPick: pick, onWriteIn: writeIn },
          onPrimary: (party) => void linkTo({ raceId: link.data!.raceId, writeIn: null }, party).catch(() => undefined),
        }
      : null;

  let body;
  if (link.isPending) body = <p className="text-muted-foreground">Loading your race…</p>;
  else if (link.isError) body = <Refusal error={link.error} />;
  else if (!link.data)
    body = mayEdit ? (
      <VoteGoalCalculator opening={{ state: otherState, race: null, edit: edit(null) }} stages={[]} drafts={{}} onDraft={() => undefined} />
    ) : (
      <p className="text-muted-foreground">The campaign hasn’t picked its race yet. A campaign admin can pick it here.</p>
    );
  else {
    body = (
      <>
        <LinkedGoal key={link.data.raceId} link={link.data} otherState={otherState} edit={edit(otherState ? null : link.data)} />
        {otherState && (
          <button type="button" className="text-sm font-semibold text-primary hover:underline" onClick={keepRace}>
            Keep {link.data.raceName}
          </button>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 pb-32">
      <p className="text-sm text-muted-foreground">
        Race picture / <b className="text-foreground">Your vote goal</b>
      </p>
      {linkError && <Refusal error={linkError} />}
      {body}
      {linking && <p className="text-sm text-muted-foreground">Saving your race…</p>}
      <AlertDialog open={!!switchTo} onOpenChange={(open) => !open && keepRace()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to {switchTo?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Your goals for {link.data?.raceName} stay in the campaign’s history. You’ll set a new goal for {switchTo?.name}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep {link.data?.raceName}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (switchTo) void linkTo(switchTo.choice, null).catch(() => undefined);
                setSwitchTo(null);
              }}
            >
              Switch race
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type SaveState = { kind: 'idle' } | { kind: 'refused'; error: Error };

/** The linked race's goal. While another state is picked, the sentence waits for a race there and the goal stays as it was. */
function LinkedGoal({ link, otherState, edit }: { link: CampaignRaceDto; otherState: string | null; edit: OpeningEdit | null }) {
  const placesNow = (kind: RaceStageKind) => placesOf(link.race.stages.find((s) => s.kind === kind)!.rule);
  const [drafts, setDrafts] = useState<Partial<Record<RaceStageKind, StageDraft>>>(() =>
    Object.fromEntries(
      link.stages.flatMap(({ stage, current }) => {
        if (!current) return [];
        const draft = draftFromSaved(current.yours);
        return [[stage, placesOf(current.ours.rule) === placesNow(stage) ? draft : reseated(draft)]];
      }),
    ),
  );
  // Editing a written-in race keeps its id, so this goal stays on screen when its seats change.
  const seats = link.race.stages.map((s) => placesOf(s.rule)).join();
  const [seatsShown, setSeatsShown] = useState(seats);
  if (seats !== seatsShown) {
    setSeatsShown(seats);
    setDrafts(reseatAll);
  }
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });
  const { mutateAsync: saveGoal, isPending: saving } = useSaveVoteGoal();

  const stages = link.stages.map((goal) => ({ goal, stage: link.race.stages.find((s) => s.kind === goal.stage)! }));
  const works = stages.map(({ goal, stage }) => ({ goal, stage, work: workOut(stage, drafts[stage.kind] ?? emptyDraft()) }));
  const ready = works.filter((w) => w.work.kind === 'Ready');
  const changed = ready.map(({ goal, stage, work }) => saveRequestOf(link.raceId, stage, goal.stageVersion, work)).filter((request, i) => !sameAsSaved(request, ready[i].goal.current));
  const primary = link.primary;
  const needsAnswer = primary.status === RacePrimaryStatus.NeedsAnswer || primary.status === RacePrimaryStatus.AnswerGone;
  const asked = needsAnswer || primary.status === RacePrimaryStatus.Chosen;
  const retired = link.standing === RaceStanding.Retired;
  const raceShown = otherState === null;
  const opening: RaceOpening = {
    state: otherState ?? stateOf(link.raceId),
    race: raceShown ? { name: link.raceName, primary: asked ? { answer: needsAnswer ? null : primary.answer } : null } : null,
    edit,
  };
  const mayEdit = edit !== null;

  const save = async () => {
    setSaveState({ kind: 'idle' });
    try {
      for (const request of changed) await saveGoal(request);
    } catch (error) {
      setSaveState({ kind: 'refused', error: error as Error });
    }
  };

  return (
    <>
      {retired && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          We no longer publish {link.raceName}. Your saved goals stay; pick your race again to set a new one.
        </p>
      )}
      {!mayEdit && needsAnswer && <p className="text-muted-foreground">The campaign hasn’t said which primary it runs in yet. A campaign admin can answer it here.</p>}

      <VoteGoalCalculator
        opening={opening}
        stages={stages.map((s) => s.stage)}
        drafts={drafts}
        onDraft={(kind, draft) => {
          setDrafts((d) => ({ ...d, [kind]: draft }));
          setSaveState({ kind: 'idle' });
        }}
        writtenIn={link.writtenIn}
      />
      {raceShown && primary.status === RacePrimaryStatus.AnswerGone && (
        <p className="text-sm text-muted-foreground">This race no longer has the {primary.answer} primary you picked before.</p>
      )}

      {/* Both goals wait for the answer, so nobody saves the general's alone. */}
      {openingComplete(opening) && (
        <>
          {saveState.kind === 'refused' && <Refusal error={saveState.error} />}

          <div className="sticky bottom-0 -mx-6 flex items-center justify-between gap-3 border-t bg-background/90 px-6 py-2 backdrop-blur sm:py-4">
            <p className="min-w-0 text-xs font-semibold tabular-nums sm:text-sm">
              {works.map(({ stage, work }, i) => (
                <span key={stage.kind}>
                  {i > 0 && ' · then '}
                  {stageTitle(stage.kind)} {work.kind === 'Ready' ? fmt(work.aim) : 'needs your numbers'}
                </span>
              ))}
            </p>
            {mayEdit ? (
              <div className="flex shrink-0 items-center gap-3">
                <PendingButton pending={saving} disabled={!changed.length || retired} onClick={() => void save()}>
                  {ready.length && !changed.length ? 'Saved' : ready.length > 1 ? 'Save my goals' : 'Save my goal'}
                </PendingButton>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Only a campaign admin can save the goal.</p>
            )}
          </div>
        </>
      )}
    </>
  );
}

function Refusal({ error }: { error: Error }) {
  return (
    <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
      <p className="font-medium text-destructive">{error.message}</p>
      {error instanceof ApiError && typeof error.body.nextAction === 'string' && <p className="mt-1 text-foreground">{error.body.nextAction}</p>}
    </div>
  );
}
